import log from 'loglevel';
import { SITE_ROOT } from '../api-config.mjs';

// Microsoft Clarity ships its session-replay telemetry to many vendor hostnames
// (www.clarity.ms, [a-z].clarity.ms, c.clarity.ms, c.bing.com), all of which ad
// blockers drop by default. The SDK has no proxy option, so we rewrite every
// Clarity-bound request to a first-party path (proxied by CloudFront back to the
// real host — see /clr/* and /cbing/* in the cca repo backend.ts). The loader
// <script> is self-injected at a first-party src (initClarity); the runtime's
// telemetry (fetch / XHR / sendBeacon) is rewritten by installClarityProxy.

let installed = false;

// Map a Clarity-bound absolute URL to its first-party proxy path. Returns null
// for any URL that is not Clarity-related (so it passes through untouched).
export function rewriteClarityUrl(rawUrl) {
  let url;
  try {
    url = new URL(rawUrl, window.location.href);
  } catch (err) {
    return null;
  }
  if (url.hostname === 'c.bing.com') {
    return `${SITE_ROOT}/cbing${url.pathname}${url.search}`;
  }
  const match = url.hostname.match(/^([a-z0-9-]+)\.clarity\.ms$/);
  if (match) {
    return `${SITE_ROOT}/clr/${match[1]}${url.pathname}${url.search}`;
  }
  return null;
}

export function installClarityProxy() {
  if (installed || typeof window === 'undefined') {
    return;
  }
  installed = true;

  const originalFetch = window.fetch && window.fetch.bind(window);
  if (originalFetch) {
    window.fetch = (input, init) => {
      try {
        const url = typeof input === 'string' ? input : input && input.url;
        const rewritten = url ? rewriteClarityUrl(url) : null;
        if (rewritten) {
          return typeof input === 'string'
            ? originalFetch(rewritten, init)
            : originalFetch(new Request(rewritten, input), init);
        }
      } catch (err) {
        log.debug('clarityProxy fetch rewrite failed', err);
      }
      return originalFetch(input, init);
    };
  }

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    const rewritten = typeof url === 'string' ? rewriteClarityUrl(url) : null;
    return originalOpen.call(this, method, rewritten || url, ...rest);
  };

  if (navigator.sendBeacon) {
    const originalSendBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = (url, data) => {
      const rewritten = typeof url === 'string' ? rewriteClarityUrl(url) : null;
      return originalSendBeacon(rewritten || url, data);
    };
  }
}

export function initClarity(projectId) {
  if (!projectId || typeof document === 'undefined') {
    return;
  }
  // Classic Clarity loader snippet, but with a first-party src so the loader
  // <script> request is not blocked. (A fetch/XHR patch can't intercept a
  // <script src>, hence we point it at the proxy path ourselves.)
  const inject = () => {
    (function (c, l, a, r, i) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      const t = l.createElement(r);
      t.async = 1;
      t.src = `${SITE_ROOT}/clr/www/tag/${i}`;
      (l.head || l.documentElement).appendChild(t);
    })(window, document, 'clarity', 'script', projectId);
  };
  // Defer until window load. The proxied tag itself injects a second <script>
  // hardcoded to https://scripts.clarity.ms/<ver>/clarity.js, which no proxy
  // can intercept. A script inserted before the load event delays that event,
  // so when scripts.clarity.ms hangs the tab spinner, our onWindowLoadEvent
  // page-view beacon, and every Puppeteer page.goto (default waitUntil: 'load')
  // hang with it. Measured 2026-09-10 with a never-responding script server:
  // inject-before-load stalled page.goto to its navigation timeout; inject-on-
  // load resolved in 8ms. Telemetry has no business blocking page load.
  if (document.readyState === 'complete') {
    inject();
  } else {
    window.addEventListener('load', inject, { once: true });
  }
}
